import { authenticatedInstance } from '../index/index.service';
import { defaultStreamUrl, subscribeStream } from '../sse-subscribe/sse-subscribe.util';
import type {
  ApprovalRequestView,
  AreaTableView,
  AreaViewBase,
  AreaWithTablesView,
  CategoryView,
  CreditTransactionView,
  EmployeeEventView,
  FloorBackground,
  HappyHourView,
  OrderView,
  OrderlineView,
  PaymentTerminalView,
  PaymentlineView,
  PrinterView,
  ProductView,
  SessionView,
  VariantComboGroupView,
} from 'shared-models/browser';

// Summary: Single-channel multiplexed POS event union with a strict accepted-type allow-list.
// Details: docs/function-library/web/services.md#pos-stream-service
export type PosStreamEvent =
  | { type: 'order.opened'; order: OrderView }
  | { type: 'order.upserted'; order: OrderView }
  | { type: 'order.closed'; orderId: number }
  | { type: 'orderline.added'; orderId: number; orderline: OrderlineView }
  | { type: 'orderline.updated'; orderId: number; orderline: OrderlineView; previousQuantity: number; previousUnitPrice?: number }
  | { type: 'orderline.item_payment_updated'; orderId: number; items: Array<{ orderlineId: number; paidQuantity: number }> }
  | { type: 'orderline.removed'; orderId: number; orderlineId: number; lineTotal: number }
  | { type: 'orderline.sent'; orderId: number; sentLineIds: number[]; skippedLineIds: number[] }
  | { type: 'paymentline.created'; orderId: number; paymentline: PaymentlineView }
  | { type: 'paymentline.done'; orderId: number; paymentlineId: number }
  | { type: 'credit.tap.pending'; orderId: number; paymentlineId: number }
  | { type: 'credit.tap.released'; orderId: number; paymentlineId: number }
  | { type: 'credit.transaction.pending'; orderId: number; paymentlineId: number }
  | {
      type: 'credit.transaction.resolved';
      orderId: number;
      paymentlineId: number;
      paymentlineStatus: 'new' | 'done' | 'canceled_split' | 'canceled_reversed';
      orderClosed: boolean;
      transaction: CreditTransactionView;
    }
  | { type: 'session.opened'; session: SessionView }
  | { type: 'session.closed'; sessionId: number; autoClosed: boolean }
  | { type: 'cashDrawer.notConfigured' }
  | { type: 'session.daily_brief_updated'; sessionId: number; dailyBrief: string | null; updatedByName: string }
  | { type: 'session.auto_close_time_updated'; tenantId: string; autoCloseTime: string | null }
  | { type: 'tenant.floor_background_updated'; tenantId: string; floorBackground: FloorBackground }
  | {
      type: 'tenant.general_item_requires_manager_approval_updated';
      tenantId: string;
      generalItemRequiresManagerApproval: boolean;
    }
  | { type: 'session.cash_alterations_updated'; sessionId: number; cashAlterations: number }
  | { type: 'area.upserted'; area: AreaViewBase | AreaWithTablesView }
  | { type: 'table.upserted'; areaId: number; table: AreaTableView }
  | { type: 'table.removed'; areaId: number; tableId: number }
  | { type: 'employee.upserted'; employee: EmployeeEventView }
  | { type: 'employee.deactivated'; employeeId: number }
  | { type: 'employee.kicked'; employeeId: number; kickedSessionId: string }
  | { type: 'product.upserted'; product: ProductView }
  | { type: 'product.deactivated'; productId: number }
  | { type: 'payment-terminal.upserted'; terminal: PaymentTerminalView }
  | { type: 'payment-terminal.active-changed'; terminalId: number; isActive: boolean }
  | { type: 'category.upserted'; category: CategoryView }
  | { type: 'printer.upserted'; printer: PrinterView }
  | { type: 'happy_hour.changed'; happyHour: HappyHourView }
  | { type: 'variantComboGroup.upserted'; productId: number; group: VariantComboGroupView }
  | { type: 'variantComboGroup.removed'; productId: number; groupId: number }
  | { type: 'merge.links.changed' }
  | { type: 'approval_request.created'; orderId: number; request: ApprovalRequestView }
  | { type: 'approval_request.decided'; orderId: number; request: ApprovalRequestView }
  | { type: 'tenant.maintenance-mode-changed'; maintenanceMode: boolean }
  | { type: 'pos.refresh-requested'; sessionId: number | null }
  | { type: 'employee.presence.probe.requested'; probeId: string; targetSessionId: string };

export const POS_STREAM_EVENT_TYPES: ReadonlyArray<PosStreamEvent['type']> = [
  'order.opened',
  'order.closed',
  'order.upserted',
  'orderline.added',
  'orderline.updated',
  'orderline.item_payment_updated',
  'orderline.removed',
  'orderline.sent',
  'paymentline.created',
  'paymentline.done',
  'credit.tap.pending',
  'credit.tap.released',
  'credit.transaction.pending',
  'credit.transaction.resolved',
  'session.opened',
  'session.closed',
  'cashDrawer.notConfigured',
  'session.daily_brief_updated',
  'session.auto_close_time_updated',
  'tenant.floor_background_updated',
  'tenant.general_item_requires_manager_approval_updated',
  'session.cash_alterations_updated',
  'area.upserted',
  'table.upserted',
  'table.removed',
  'employee.upserted',
  'employee.deactivated',
  'employee.kicked',
  'product.upserted',
  'product.deactivated',
  'payment-terminal.upserted',
  'payment-terminal.active-changed',
  'category.upserted',
  'printer.upserted',
  'happy_hour.changed',
  'variantComboGroup.upserted',
  'variantComboGroup.removed',
  'merge.links.changed',
  'approval_request.created',
  'approval_request.decided',
  'tenant.maintenance-mode-changed',
  'pos.refresh-requested',
  'employee.presence.probe.requested',
];
// Summary: Mints a single-use HMAC-signed SSE ticket bound to tenant+role for the multiplexed POS stream so the JWT employee-session token...
// Details: docs/function-library/web/services.md#pos-stream-service
async function mintPosStreamTicket(tenantId: string, employeeSession: string): Promise<string> {
  const response = await authenticatedInstance.post<{ ticket: string; expiresAt: number }>(
    `/pos/tenant/${encodeURIComponent(tenantId)}/stream-ticket`,
    {},
    { headers: { 'x-employee-session': employeeSession } },
  );
  if (!response.data?.ticket) {
    throw new Error('POS stream ticket missing in response');
  }
  return response.data.ticket;
}

// Summary: Manager-only POST asking the backend to fan out a refresh request to all tenant devices.
export async function broadcastPosRefresh(tenantId: string, employeeSession: string): Promise<void> {
  await authenticatedInstance.post(
    `/pos/tenant/${encodeURIComponent(tenantId)}/refresh-broadcast`,
    {},
    { headers: { 'x-employee-session': employeeSession } },
  );
}

// Summary: Strict per-event payload validation. The type allowlist alone does not guarantee the
export function hasValidEventPayload(event: PosStreamEvent): boolean {
  switch (event.type) {
    case 'table.upserted':
      return (
        typeof event.areaId === 'number' &&
        Number.isFinite(event.areaId) &&
        !!event.table &&
        typeof event.table === 'object' &&
        typeof (event.table as { id?: unknown }).id === 'number'
      );
    case 'table.removed':
      return (
        typeof event.areaId === 'number' &&
        Number.isFinite(event.areaId) &&
        typeof event.tableId === 'number' &&
        Number.isFinite(event.tableId)
      );
    case 'tenant.maintenance-mode-changed':
      return typeof event.maintenanceMode === 'boolean';
    default:
      return true;
  }
}

// Summary: Opens the multiplexed per-tenant POS SSE channel for POS, areas, employees, and products events.
// Details: docs/function-library/web/services.md#pos-stream-service
export function subscribePosStream(
  tenantId: string,
  employeeSession: string,
  onEvent: (event: PosStreamEvent) => void,
  onEventError?: (error: unknown) => void,
  onError?: () => void,
  onResync?: () => void,
  onRecovery?: () => void,
  onOpen?: () => void,
): () => void {
  return subscribeStream<PosStreamEvent>({
    mintTicket: () => mintPosStreamTicket(tenantId, employeeSession),
    buildStreamUrl: (ticket, lastEventId) =>
      defaultStreamUrl(`/pos/tenant/${encodeURIComponent(tenantId)}/stream`, ticket, lastEventId),
    parseEvent: (payload) => {
      const candidate = payload as PosStreamEvent | undefined;
      if (
        candidate &&
        typeof candidate.type === 'string' &&
        POS_STREAM_EVENT_TYPES.includes(candidate.type) &&
        hasValidEventPayload(candidate)
      ) {
        return candidate;
      }
      return null;
    },
    onEvent,
    onEventError,
    onError,
    onOpen,
    onResync,
    onRecovery,
  });
}

